import { useEffect, useState } from "react";
import axios from "axios";
import { ROLE_ENDPOINTS } from "../usersListConstants";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 1;

/**
 * Fetches the role-scoped lightweight search endpoint and pushes results.
 * Each endpoint returns up to 10 rows of {id, name, email} and scopes the
 * role server-side, so no client-side role filtering is needed.
 * @param {Object} args - { endpoint, term, signal, setItems, setError, setLoading }
 * @returns {Promise<void>}
 */
async function fetchBrowse({ endpoint, term, signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get(`${endpoint}/${encodeURIComponent(term)}`);
        if (!signal.aborted) setItems(Array.isArray(data) ? data : []);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible d'effectuer la recherche");
            setItems([]);
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Debounced, role-aware lightweight users browse/search hook. The list stays
 * empty (search-first) until at least one character is typed; then it queries
 * the endpoint matching the active role tab. This avoids ever fetching the
 * heavy nested profile payloads for the list.
 * @param {Object} args
 * @param {string} args.query - Current search query
 * @param {string} args.roleFilter - Active role tab ("all" | "artist" | "auditor")
 * @param {number} [args.reloadKey] - Bump to force a re-fetch of the active query
 * @returns {{ items: Array, loading: boolean, error: string|null, active: boolean }}
 */
function useUsersBrowse({ query, roleFilter, reloadKey = 0 }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const trimmed = (query || "").trim();
    const active = trimmed.length >= MIN_QUERY_LENGTH;
    const endpoint = ROLE_ENDPOINTS[roleFilter] || ROLE_ENDPOINTS.all;

    useEffect(() => {
        if (!active) { setItems([]); setError(null); setLoading(false); return undefined; }
        const controller = new AbortController();
        const handle = setTimeout(() => {
            fetchBrowse({ endpoint, term: trimmed, signal: controller.signal, setItems, setError, setLoading });
        }, SEARCH_DEBOUNCE_MS);
        return () => { clearTimeout(handle); controller.abort(); };
    }, [trimmed, endpoint, active, reloadKey]);

    return { items, loading, error, active };
}

export default useUsersBrowse;
